import React, { useEffect, useState } from 'react'

import { useParams } from 'react-router'
import { cloneDeep } from 'lodash'

import CompetitorEventRegisterWrapper from '../../CompetitorEventRegisterWrapper'
import { Peoples } from './components/Peoples'
import { Horses } from './components/Horses'

import { getConvertedData, getSelectedUserAsTeamMember } from '../../../../models/interface.helper'
import {
  getHorseProfileReqField,
  getUserProfileReqField,
  hasInvalidRequiredField,
} from '../../../../helpers/requiredFields'
import useHorses from '../../../../hooks/users/competitor/profile/useHorses'
import useToasterHelper from '../../../../helpers/ToasterHelper'
import { selectProfileData } from '../../../../store/user/userSlice'

import FirestoreService from '../../../../services/firestoreService'
import HorsesService from '../../../../services/firebase-service/horses/HorsesService'
import UserService from '../../../../services/firebase-service/users/userService'

import { selectedEvent } from '../../../../store/events/eventsSlice'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import {
  selectRegistration,
  selectTeamTabData,
  setHorseRegistrationErrors,
  setTeamTabData,
  setUserRegistrationErrors,
} from '../../../../store/registration/registrationSlice'
import { selectHorses, setHorses } from '../../../../store/horses/horseSlice'

import ITypes from '../../event-registration-tabs/hooks/useEventRegistrationTabs.types'
import { UserHorseMappingModel } from '../../../../models/user-horse-mapping/userHorseMapping.model'
import { IHorseCompetitorDocument } from '../../../../models/horse-competiton-paperwork/horse-competiton-paperwork-interface'
import { IUserPublicInterface } from '../../../../models/users-public/users-public.interface'
import { IUserHorseMappingInterface } from '../../../../models/user-horse-mapping/userHorseMapping.interface'
import { ITeamMemberR } from '../../../../models/event-registered-users/event-registered-users.interface'

import { CONST } from '../../../../const/const'
import { REQUIRED_FIELDS } from '../../../../const/required-fields-const'
import { USE_MODE_CONST } from '../../../../const/use-mode'

const COLLECTIONS = CONST.DATA.FIRESTORE.LATEST.COLLECTIONS

type ClinicNOtherStatusTabProps = {
  title?: string
  description?: string
}

const ClinicNOtherStatusTab = (props: ClinicNOtherStatusTabProps) => {
  const { userId } = useParams<ITypes['IParams']>()
  const toastFunction = useToasterHelper()

  const dispatch = useAppDispatch()
  const { getMapping, getTeamMembersHorses } = useHorses()
  const { Event } = useAppSelector(selectedEvent)
  const teamTabData = useAppSelector(selectTeamTabData)
  const myHorses = useAppSelector(selectHorses)
  const registration = useAppSelector(selectRegistration)

  const profileDetails = useAppSelector(selectProfileData)

  const [teamTabDataWithUser, setTeamTabDataWithUser] = useState<IUserPublicInterface[]>([])
  const [mainLoading, setMainLoading] = useState(true)
  const [horsesPaperwork, setHorsesPaperwork] = useState<IHorseCompetitorDocument[] | null>(null)
  const [mapping, setMapping] = useState<IUserHorseMappingInterface[] | null>(null)

  const getTeamDataFromDb = async (horsesTeamsMappings: IUserHorseMappingInterface[]) => {
    let teams: ITeamMemberR[] = []
    let teamsIds: Set<string> = new Set()

    if (registration?.membersIds?.[0]) {
      registration?.membersIds.forEach((membersId) => {
        teamsIds.add(membersId)
      })
    } else {
      horsesTeamsMappings.forEach((item) => {
        Array.isArray(item.teamMembers) &&
          item.teamMembers.forEach((member) => {
            if (member.memberId) teamsIds.add(member.memberId)
          })
      })

      profileDetails.userTeamMembers?.forEach((member) => {
        if (member.memberId && ![...teamsIds].includes(member.memberId))
          teamsIds.add(member.memberId)
      })
    }

    const usersInDb = await UserService.getByIds([...teamsIds, userId])
    const registrar = await UserService.getUserById(userId)

    if (!registrar?.id) {
      toastFunction.success({
        message: 'Registrar not found',
      })
      return
    }

    teamsIds.forEach((id) => {
      const user = usersInDb.find((u) => u.id === id && u.userType !== USE_MODE_CONST.ORGANIZER)
      if (user) {
        const current = getConvertedData(getSelectedUserAsTeamMember({ ...user }, registrar))
        teams.push(current)
      }
    })

    // we need to add registrar as team member
    const registrarAsTeamMember = getConvertedData(
      getSelectedUserAsTeamMember(registrar, profileDetails)
    )
    teams.push(registrarAsTeamMember)

    dispatch(setTeamTabData([...teams]))
  }

  const getHorsesFromDB = async (horsesTeamsMappings: IUserHorseMappingInterface[]) => {
    let horsesIds: string[] = []

    try {
      if (registration?.horsesIds?.[0]) {
        horsesIds = cloneDeep(registration.horsesIds)
      } else {
        const teamMembersHorses = await getTeamMembersHorses()
        if (profileDetails?.userType === CONST.USE_MODE.ORGANIZER) {
          horsesIds = [...teamMembersHorses.map((horse) => horse.id ?? '')]
        } else {
          horsesIds = [
            ...horsesTeamsMappings.map((horse) => horse.horseId ?? ''),
            ...teamMembersHorses.map((horse) => horse.id ?? ''),
          ]
        }
      }
      const newHorses = await HorsesService.getHorsesByIds(horsesIds)
      dispatch(setHorses(newHorses))
    } catch (error) {
      console.error('error', error)
    }
  }

  const getHMappingFromDB = async () => {
    const mapping_: IUserHorseMappingInterface[] = []

    const mappingSnaps = await FirestoreService.filterItems(COLLECTIONS.USER_HORSE_MAPPING.NAME)
    mappingSnaps.forEach((currDoc) => {
      const horseMapping = UserHorseMappingModel.fromFirestoreDoc(currDoc).toObject()
      mapping_.push(horseMapping)
    })
    setMapping(mapping_)
  }

  const loadAllData = async () => {
    setMainLoading(true)
    const { horsesTeamsMappings } = await getMapping(userId)
    await Promise.all([
      getTeamDataFromDb(horsesTeamsMappings),
      getHorsesFromDB(horsesTeamsMappings),
    ])
    setMainLoading(false)
  }

  useEffect(() => {
    if (Event?.id && userId) loadAllData().then()
  }, [Event?.id, userId, registration])

  useEffect(() => {
    getHMappingFromDB().then()
  }, [Event?.id, myHorses])

  useEffect(() => {
    let hasHorseError = false
    const requiredFiled = [
      ...REQUIRED_FIELDS.horseMain.filter((itm) => Event?.requiredHorseFields.includes(itm.value)),
      ...REQUIRED_FIELDS.horseSecondary.filter((itm) =>
        Event?.requiredHorseFields.includes(itm.value)
      ),
      ...REQUIRED_FIELDS.horseNumbers.filter((itm) =>
        Event?.requiredHorseFields.includes(itm.value)
      ),
    ]

    myHorses.forEach((horse) => {
      requiredFiled.forEach((field) => {
        const currentMapping = mapping?.find((mapping_) => mapping_.horseId === horse?.id)

        const horsePaperwork = horsesPaperwork?.find(
          (paperwork) => paperwork.id && paperwork.id === currentMapping?.id
        )

        const profileReqField = getHorseProfileReqField({
          horse,
          field,
          horsePaperwork: horsePaperwork?.id ? horsePaperwork : null,
        })
        if (hasInvalidRequiredField(profileReqField)) hasHorseError = true
      })
    })
    dispatch(setHorseRegistrationErrors(hasHorseError))
  }, [myHorses, Event?.requiredHorseFields, mapping, horsesPaperwork])

  useEffect(() => {
    let hasUserError = false
    const requiredFiled = [
      ...REQUIRED_FIELDS.people.filter((itm) => Event?.requiredFields.includes(itm.value)),
      ...REQUIRED_FIELDS.peopleNumbers.filter((itm) => Event?.requiredFields.includes(itm.value)),
    ]

    teamTabDataWithUser.forEach((user) => {
      requiredFiled.forEach((field) => {
        const profileReqField = getUserProfileReqField({ user, field })
        if (hasInvalidRequiredField(profileReqField)) hasUserError = true
      })
    })

    dispatch(setUserRegistrationErrors(hasUserError))
  }, [teamTabDataWithUser, Event?.requiredFields])

  useEffect(() => {
    const updateTeamData = async () => {
      const team: IUserPublicInterface[] = []

      let ids: Set<string> = new Set()
      teamTabData.forEach((member) => {
        if (member.memberId) ids.add(member.memberId)
      })
      const usersInDb: IUserPublicInterface[] = await UserService.getByIds([...ids, userId])

      teamTabData.forEach((member) => {
        const current = usersInDb.find(
          (user) => user.id === member.memberId && user.userType !== USE_MODE_CONST.ORGANIZER
        )
        if (current?.id) team.push(current)
      })

      setTeamTabDataWithUser(team)
    }
    updateTeamData().then()
  }, [teamTabData, userId])

  return (
    <>
      <CompetitorEventRegisterWrapper title={props.title} description={props.description}>
        <div className="flex justify-between flex-wrap gap-5">
          <Peoples
            teamTabDataWithUser={teamTabDataWithUser}
            setTeamTabDataWithUser={setTeamTabDataWithUser}
            mainLoading={mainLoading}
          />
          <Horses
            mainLoading={mainLoading}
            horsesPaperwork={horsesPaperwork}
            setHorsesPaperwork={setHorsesPaperwork}
            mapping={mapping}
          />
        </div>
      </CompetitorEventRegisterWrapper>
    </>
  )
}

export default ClinicNOtherStatusTab
